<template lang="pug">
md-layout(md-flex="100")
    .md-headline Subtitles
    loader(:loading='true', :size='40', v-if="anyRowLoading")

    md-layout.new-subtitle-row(md-flex="100")
        md-layout.subtitle-row(
            v-for="(row, index) in formRows"
            :key="'form-' + index"
            md-flex="100"
        )
            md-layout.options.flex-grow-0
                md-select(v-if="anyRowLoading" key="select-disabled" v-model="row.language" placeholder="Select language" disabled)
                md-select(v-else key="select-enabled" v-model="row.language" placeholder="Select language")
                    md-option(
                        v-for="language in languagesForRow(index)"
                        :key="language.code"
                        :value="language.code"
                    )
                        | {{ language.name }}
            md-layout
                md-input-container()
                    label Select .srt file to upload
                    md-file(
                        :key="'file-input-' + index + '-' + fileInputResetKeys[index]"
                        type="file"
                        accept=".srt"
                        :disabled='anyRowLoading || !row.language'
                        @selected="onFileSelected($event, index)"
                    )

    
    div.container-info(v-if="isEditMode && changeProp")
        md-icon.icon-info(md-iconset="fa fa-info")
        p.check-properties() Changes will be applied when you click update.

    div.subtitles-section
        md-layout.subtitle-row(
            v-for="(subtitle, index) in subtitlesProp"
            :key="'uploaded-' + index"
            md-flex="100"
        )
            md-layout.options.flex-grow-0
                md-select(:value="subtitle.language" disabled placeholder="Language")
                    md-option(
                        v-for="language in availableLanguages"
                        :key="language.code"
                        :value="language.code"
                    )
                        | {{ language.name }}
            md-layout()
                span.subtitle-link
                    a(:href="'//' + subtitleBucketName + '/' + subtitle.path", target="_blank", rel="noopener noreferrer")
                        | {{ subtitle.path.split('/').pop() }}
            md-layout.row-actions.flex-grow-0
                md-button.md-icon-button.md-primary(@click="deleteUploadedRow(subtitle)")
                    md-icon.icon(md-iconset="fa fa-trash")


</template>

<script>
import uploadService from '../../services/uploadService';
import { mapState } from 'vuex';
import Loader from '../Loader.vue';
import subtitleLanguages from '../../common/objects/subtitleLanguages';

export default {
    components: {
        Loader,
    },
    props: {
        subtitlesProp: {
            type: Array,
            required: false,
            default: () => [],
        },
        isEditMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            subtitleBucketName: process.env.SUBTITLES_BUCKET_NAME.replace('-rhebus-works', '.rhebus.works'),
            availableLanguages: subtitleLanguages,
            formRows: [
                { language: null, file: null, loading: false },
            ],
            fileInputResetKeys: [0],
            changeProp: false,
            isFirstLoad: true,
        };
    },

    computed: {
        ...mapState({
            accountID: (state) => state.user.siteuser.user_metadata.account_id,
        }),
        anyRowLoading() {
            return this.formRows.some((row) => row.loading);
        },
        usedLanguageCodes() {
            const fromUploaded = this.subtitlesProp.map((s) => s.language);
            const fromFormRows = this.formRows.map((r) => r.language).filter(Boolean);
            return new Set([...fromUploaded, ...fromFormRows]);
        },
    },

    methods: {

        languagesForRow(index) {
            const currentLang = this.formRows[index] && this.formRows[index].language;
            return subtitleLanguages.filter(
                (lang) => lang.code === currentLang || !this.usedLanguageCodes.has(lang.code)
            );
        },

        addFormRow(fileInputResetKey = 0) {
            this.formRows.push({ language: null, file: null, loading: false });
            this.fileInputResetKeys.push(fileInputResetKey);
        },

        removeFormRow(index) {
            const nextFileInputResetKey = (this.fileInputResetKeys[index] || 0) + 1;
            this.formRows.splice(index, 1);
            this.fileInputResetKeys.splice(index, 1);
            this.addFormRow(nextFileInputResetKey);
        },

        resetFileInput(index) {
            this.formRows[index].file = null;
            this.fileInputResetKeys.splice(index, 1, this.fileInputResetKeys[index] + 1);
        },

        deleteUploadedRow(subtitle) {
            this.$emit('delete-subtitle', { language: subtitle.language, path: subtitle.path });
        },

        onFileSelected(files, index) {
            if (!files || files.length === 0) return;

            const file = files[0];
            if (!/\.(srt)$/i.test(file.name)) {
                this.resetFileInput(index);
                alert('Please select a valid .srt file.');
                return;
            }

            this.formRows[index].file = file;
            this.handleRowUpload(index);
        },

        async handleRowUpload(index) {
            const row = this.formRows[index];
            if (!row.file) {
                alert('No subtitle file selected.');
                return;
            }
            if (!row.language) {
                alert('No language selected.');
                return;
            }

            row.loading = true;
            try {
                const uploadUrl = await uploadService.uploadSubtitles(
                    { language: row.language, file: row.file },
                    this,
                    this.accountID
                );
                this.$emit('subtitle-uploaded', { language: row.language, path: uploadUrl });
                this.$swal({
                    text: 'Subtitle uploaded successfully',
                    position: 'bottom-left',
                    icon: 'success',
                    toast: true,
                    timer: 3000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
                this.removeFormRow(index);
            } catch (e) {
                alert('Subtitle upload failed. Please try again.');
            } finally {
                row.loading = false;
            }
        },
    },

    watch: {
        subtitlesProp() {
            if (this.isFirstLoad) {
                this.isFirstLoad = false;
            } else {
                this.changeProp = true;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.subtitles-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
}

.centered {
    margin: 0 auto;
}

.flex-grow-0 {
    flex-grow: 0;
}

.subtitle-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.options {
    margin-right: 16px;

    md-select {
        margin: 10px 0;
    }
}

.row-actions {
    display: flex;
    align-items: center;
}

.add-row {
    display: flex;
    align-items: center;
    margin-top: 4px;
}

.subtitle-actions {
    display: flex;
    align-items: center;
    margin-top: 8px;
}

.subtitle-link {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 8px 0;

    a {
        font-size: 1.1em;
        font-style: italic;
    }
}


.check-properties {
    font-style: italic;
    color: gray;
    font-weight: 500;
}

.container-info {
    display: flex;
    justify-content: flex-start;
    align-items: center;
}

.icon-info {
    justify-content: center;
    margin: 0 10px 0 0;
    font-size: small;
    background-color: #4ea9dd;
    border-radius: 50%;
}
</style>



// WEBPACK FOOTER //
// src/shared-components/upload/SubtitleUploader.vue